import type { SegmentType } from '@rcs/data-schema';
import { accelerationToG } from '@rcs/domain';
import { frameAtTime, type SimulationResult } from '@rcs/physics';
import { ToolButton } from '@rcs/ui';
import { useSyncExternalStore, type PointerEvent } from 'react';
import { messages } from '../i18n/ja';
import type { PlaybackController, PlaybackSnapshot } from '../playback/controller';
import { BOOST_ACCELERATION, BOOST_DURATION, THROTTLE_RANGE } from '../ride/control-timeline';
import type { RideControl } from '../ride/useRideControl';
import type { SampledTrack } from '@rcs/track-geometry';

/** 操作の状態（表示用）。 */
export type ThrottleState = 'paused' | 'idle' | 'accelerating' | 'boosting' | 'blocked';

/** 加速度の表示（m/s² と G）。 */
export function formatAcceleration(acceleration: number): string {
  return `${acceleration.toFixed(1)} m/s²（${accelerationToG(acceleration).toFixed(2)} G）`;
}

/** 加速度の短い表示（スライダーの横）。 */
function formatAccelerationShort(acceleration: number): string {
  return `${acceleration.toFixed(1)} m/s² · ${accelerationToG(acceleration).toFixed(2)} G`;
}

/** 操作が効く区間か（リフト・ブレーキ・駅・発進加速では効かない。物理の acceptsControl と同じ）。 */
export function controlAllowed(kind: SegmentType | undefined): boolean {
  return kind === undefined || kind === 'normal' || kind === 'block';
}

/**
 * 走行中の操作（FR-SIM-008）：押している間の加速ボタン・ブースト・加速の強さのスライダーと、状態の表示。
 * 3D ビューの右下（再生の操作の上）に重ねる。ボタンは指で押し続けやすいよう大きくする。
 */
export function RideControls({
  track,
  result,
  segmentKinds,
  controller,
  control,
  acceleration,
  onAccelerationChange,
}: {
  readonly track: SampledTrack;
  /** 操作を反映した走行の結果 */
  readonly result: SimulationResult;
  readonly segmentKinds: readonly SegmentType[] | undefined;
  readonly controller: PlaybackController;
  readonly control: RideControl;
  /** 押している間の加速度 [m/s²] */
  readonly acceleration: number;
  readonly onAccelerationChange: (acceleration: number) => void;
}) {
  const t = messages.rideControl;
  const snapshot: PlaybackSnapshot = useSyncExternalStore(
    controller.subscribe,
    controller.getSnapshot,
  );
  const frame = frameAtTime(result, snapshot.time);
  const index = Math.min(track.count - 1, Math.round(frame.s / track.spacing));
  const segment = track.closed
    ? track.segment[((index % track.count) + track.count) % track.count]
    : track.segment[index];
  const kind = segment === undefined ? undefined : segmentKinds?.[segment];
  const boosting = control.isBoosting(snapshot.time);
  const active = control.holding || boosting;
  const state: ThrottleState = !snapshot.playing
    ? 'paused'
    : active && !controlAllowed(kind)
      ? 'blocked'
      : boosting
        ? 'boosting'
        : control.holding
          ? 'accelerating'
          : 'idle';
  const sectionName =
    kind === undefined
      ? ''
      : ((messages.segmentLegend.kinds as Record<string, string>)[kind] ?? kind);
  const status =
    state === 'paused'
      ? t.paused
      : state === 'blocked'
        ? t.blocked(sectionName)
        : state === 'boosting'
          ? t.boosting
          : state === 'accelerating'
            ? t.accelerating(formatAcceleration(acceleration))
            : t.idle;
  const icon =
    state === 'blocked'
      ? '⛔'
      : state === 'boosting'
        ? '🚀'
        : state === 'accelerating'
          ? '⏩'
          : 'ℹ';

  const start = (event: PointerEvent<HTMLButtonElement>): void => {
    // 指を少し動かしても、離すまで押し続けたことにする
    event.currentTarget.setPointerCapture(event.pointerId);
    control.press();
  };
  const stop = (): void => {
    control.release();
  };

  return (
    <div
      className="ride-controls"
      role="group"
      aria-label={t.group}
      data-testid="ride-controls"
      data-controlled={control.controlled !== undefined}
      data-provisional={control.controlled?.provisional ?? false}
      data-control-count={control.count}
    >
      <p
        className="ride-controls__status"
        role="status"
        data-testid="throttle-status"
        data-state={state}
      >
        <span aria-hidden="true">{icon}</span> {status}
      </p>
      <label className="ride-controls__strength">
        <span className="rcs-visually-hidden">{t.strength}</span>
        <input
          type="range"
          min={THROTTLE_RANGE.min}
          max={THROTTLE_RANGE.max}
          step={THROTTLE_RANGE.step}
          value={acceleration}
          onChange={(event) => {
            onAccelerationChange(Number(event.target.value));
          }}
          data-testid="throttle-acceleration"
        />
        <output data-testid="throttle-acceleration-value" title={formatAcceleration(acceleration)}>
          {formatAccelerationShort(acceleration)}
        </output>
      </label>
      <div className="ride-controls__buttons">
        <ToolButton
          className="ride-controls__boost"
          label={t.boost}
          icon={<span aria-hidden="true">🚀</span>}
          title={t.boostHint(BOOST_DURATION, formatAcceleration(BOOST_ACCELERATION))}
          aria-keyshortcuts="B"
          pressed={boosting}
          disabled={!snapshot.playing}
          onClick={() => {
            control.boost();
          }}
          data-testid="boost"
        />
        <ToolButton
          className="ride-controls__throttle"
          label={t.accelerate}
          icon={<span aria-hidden="true">⏩</span>}
          title={t.accelerateHint}
          aria-keyshortcuts="ArrowUp"
          pressed={control.holding}
          disabled={!snapshot.playing}
          onPointerDown={start}
          onPointerUp={stop}
          onPointerCancel={stop}
          onLostPointerCapture={stop}
          onContextMenu={(event) => {
            // iPad の長押しメニューを出さない
            event.preventDefault();
          }}
          onKeyDown={(event) => {
            if ((event.key === ' ' || event.key === 'Enter') && !event.repeat) {
              event.preventDefault();
              control.press();
            }
          }}
          onKeyUp={(event) => {
            if (event.key === ' ' || event.key === 'Enter') {
              event.preventDefault();
              control.release();
            }
          }}
          data-testid="throttle"
        />
      </div>
    </div>
  );
}
